import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const PRESETS = [
  { label: 'This Month', getRange: () => {
    const now = new Date();
    const start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    return { startDate: start, endDate: now.toISOString().split('T')[0] };
  }},
  { label: 'This Quarter', getRange: () => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3);
    const start = `${now.getFullYear()}-${String(q * 3 + 1).padStart(2, '0')}-01`;
    return { startDate: start, endDate: now.toISOString().split('T')[0] };
  }},
  { label: 'This Year', getRange: () => {
    const now = new Date();
    return { startDate: `${now.getFullYear()}-01-01`, endDate: now.toISOString().split('T')[0] };
  }},
  { label: 'Last Year', getRange: () => {
    const now = new Date();
    const y = now.getFullYear() - 1;
    return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
  }},
  { label: 'Last 12 Months', getRange: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return { startDate: start.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
  }},
];

/**
 * Date range picker with preset quick-select options and custom date inputs.
 * Calls onChange({ startDate, endDate }) when the range changes.
 */
export default function DateRangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState(value?.startDate || '');
  const [customEnd, setCustomEnd] = useState(value?.endDate || '');

  const selectPreset = (preset) => {
    const range = preset.getRange();
    setCustomStart(range.startDate);
    setCustomEnd(range.endDate);
    onChange(range);
    setOpen(false);
  };

  const applyCustom = () => {
    if (customStart && customEnd) {
      onChange({ startDate: customStart, endDate: customEnd });
      setOpen(false);
    }
  };

  const formatDisplay = () => {
    if (value?.startDate && value?.endDate) {
      const s = new Date(value.startDate + 'T00:00:00');
      const e = new Date(value.endDate + 'T00:00:00');
      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${fmt(s)} – ${fmt(e)}`;
    }
    return 'Select period';
  };

  return (
    <div className="drp-container">
      <button type="button" className="drp-trigger" onClick={() => setOpen(o => !o)}>
        <Calendar size={13} />
        <span className="drp-trigger-text">{formatDisplay()}</span>
        <ChevronDown size={12} className={`drp-chevron ${open ? 'open' : ''}`} />
      </button>

      {open && (
        <>
          <div className="drp-backdrop" onClick={() => setOpen(false)} />
          <div className="drp-dropdown">
            <div className="drp-section-label">Quick Select</div>
            {PRESETS.map((p) => (
              <button key={p.label} type="button" className="drp-preset" onClick={() => selectPreset(p)}>
                {p.label}
              </button>
            ))}
            <div className="drp-divider" />
            <div className="drp-section-label">Custom Range</div>
            <div className="drp-custom">
              <div className="drp-field">
                <label>From</label>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
              </div>
              <div className="drp-field">
                <label>To</label>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
              </div>
              <button type="button" className="drp-apply" onClick={applyCustom} disabled={!customStart || !customEnd}>
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
